import makeStyles from '@mui/styles/makeStyles';
import TextField from '@mui/material/TextField';
import {get, noop} from "lodash";
import React from "react";
import {color, type} from "common/tokens";

// The same field SelectField draws, so a form reads as one set of controls
// whichever question types it happens to hold.
export const fieldStyles = {
  root: {
    ...type.body,
    background: color.SURFACE,
    borderRadius: 12,
    minHeight: 52,
    border: `1px solid ${color.BORDER}`,
    color: color.TEXT,
    '&:hover:not($disabled):not($focused):not($error)': {
      borderColor: color.TEXT_MUTED,
    },
  },
  // Marked important because three screens still wrap their questions in
  // `questionFields`, whose `.MuiInputBase-root` rule sets a resting border at a
  // specificity that would otherwise swallow these two states.
  focused: {
    borderColor: `${color.ACCENT} !important`,
  },
  error: {
    borderColor: `${color.DANGER} !important`,
  },
  disabled: {
    background: color.FIELD_BG,
    borderColor: color.DIVIDER,
    color: color.TEXT_MUTED,
  },
  // MUI draws the outline as a fieldset legend; the border above replaces it.
  notchedOutline: {
    border: 'none !important',
  },
  input: {
    ...type.body,
    color: 'inherit',
    padding: '14px 16px',
    '&::placeholder': {
      color: color.TEXT_SUBTLE,
      opacity: 1,
    },
  },
};

const useStyles = makeStyles(fieldStyles);

// Only the props the input actually needs are passed on. MUI forwards anything
// it does not recognise to the DOM, which is where the `centerOptionText` and
// `showBorder` attribute warnings in the console came from.
const TextInput = ({onAnswered = noop, question, value, error, autoComplete, placeholder, id, describedBy}) => {
  const classes = useStyles();
  const disabled = get(question, 'disabled', false);

  return (
    <TextField
      type={'text'}
      value={value ?? ''}
      onChange={event => onAnswered(event.target.value)}
      variant={'outlined'}
      fullWidth
      disabled={disabled}
      error={Boolean(error)}
      autoComplete={autoComplete}
      placeholder={placeholder}
      id={id}
      inputProps={{
        'aria-describedby': describedBy,
        'aria-invalid': Boolean(error) || undefined,
        'aria-required': get(question, 'mandatory', false) || undefined,
      }}
      InputProps={{
        classes: {
          root: classes.root,
          focused: classes.focused,
          error: classes.error,
          disabled: classes.disabled,
          notchedOutline: classes.notchedOutline,
          input: classes.input,
        },
      }}
    />
  );
};

export default TextInput;
